import { addDays, formatISO, subDays } from "date-fns";
import { toIsoDate } from "@common/helpers/date";
import type { CloseOverviewMember } from "@common/schemas/clientsCloseOverview.schema";
import { CloseHealth, CloseHealthReason } from "@common/types/closeHealth";
import { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import type { ClientsWorkOverviewWorkRow } from "@components/organizationView/clients-work/clientsWorkOverviewViewConfig";
import {
    createWorkspaceRow,
    createWorkStage,
} from "@components/organizationView/clients-work/board/workBoard.fixtures";
import { DEMO_CLIENTS, DEMO_ENTITIES, DEMO_PEOPLE } from "./demoTasks";

/** The Demo Firm's close stages, in board order. */
export const DEMO_WORK_STAGES = ["Intake", "Prep", "Review", "Deliver"].map(
    (name, index) => createWorkStage({ name, order: index + 1 }),
);

/** A demo person's user id on Work rows: their place in `DEMO_PEOPLE`, from 1. */
export const getDemoWorkUserId = (personIndex: number) => personIndex + 1;

const [AVERY, JORDAN, RILEY, SAM, MORGAN] = DEMO_PEOPLE.map(
    (person, index): CloseOverviewMember => ({
        userId: getDemoWorkUserId(index),
        role: UserWorkspaceRole.ACCOUNTANT,
        email: person.email,
        iconColor: person.iconColor,
        avatar: person.avatar,
        preferredName: person.name,
        organizationId: "demo-firm",
    }),
);

interface DemoWorkSpec {
    name: string;
    entityIndex: number;
    /** The stage's place in `DEMO_WORK_STAGES`; null for Done or Canceled work. */
    stageIndex: number | null;
    daysInStage?: number;
    /** Days from today to the work end; negative is late. */
    deliveryInDays?: number;
    tasks?: [done: number, total: number];
    team: CloseOverviewMember[];
    health?: CloseHealth;
    isCanceled?: boolean;
    isAgentRunning?: boolean;
    unreadTaskCount?: number;
}

const toWorkRow = (
    {
        name,
        entityIndex,
        stageIndex,
        daysInStage = 2,
        deliveryInDays,
        tasks = [0, 0],
        team,
        health = CloseHealth.NONE,
        isCanceled = false,
        isAgentRunning = false,
        unreadTaskCount = 0,
    }: DemoWorkSpec,
    today: Date,
): Partial<ClientsWorkOverviewWorkRow> => {
    const entity = DEMO_ENTITIES[entityIndex];
    const stage = stageIndex === null ? null : DEMO_WORK_STAGES[stageIndex];
    const [preparer, ...reviewers] = team;
    return {
        name,
        entityName: entity.name,
        entityIconColor: entity.iconColor,
        entityLogo: entity.logo,
        startDate: "2026-09-01",
        endDate: "2026-09-30",
        workDeliveryDate:
            deliveryInDays === undefined
                ? null
                : toIsoDate(addDays(today, deliveryInDays)),
        closeStageId: stage?.id ?? null,
        closeStageName: stage?.name ?? null,
        closeStageSetAt: formatISO(subDays(today, daysInStage)),
        isFinished: stageIndex === null && !isCanceled,
        cancelledAt: isCanceled ? formatISO(subDays(today, 3)) : null,
        terminatedTasksCount: tasks[0],
        totalTaskCount: tasks[1],
        preparers: [preparer],
        reviewers,
        createdByUserId: preparer.userId,
        unreadTaskCount,
        health,
        healthReason:
            health === CloseHealth.AT_RISK
                ? CloseHealthReason.DELIVERY_DATE_PASSED
                : null,
        isAgentRunning,
    };
};

/** Northwind (0) and Pinecrest (3) are the first client's, as on the Tasks board. */
const CLIENT_SPECS: ReadonlyArray<readonly DemoWorkSpec[]> = [
    [
        {
            name: "September close",
            entityIndex: 0,
            stageIndex: 1,
            daysInStage: 6,
            deliveryInDays: -3,
            tasks: [4, 11],
            team: [AVERY, JORDAN],
            health: CloseHealth.AT_RISK,
            isAgentRunning: true,
            unreadTaskCount: 2,
        },
        {
            name: "Q3 sales tax",
            entityIndex: 3,
            stageIndex: 2,
            deliveryInDays: 5,
            tasks: [6, 8],
            team: [RILEY, SAM, MORGAN],
            health: CloseHealth.ON_TRACK,
        },
        {
            name: "Payroll review",
            entityIndex: 0,
            stageIndex: null,
            tasks: [5, 5],
            team: [JORDAN],
        },
    ],
    [
        {
            name: "September close",
            entityIndex: 1,
            stageIndex: 0,
            daysInStage: 1,
            deliveryInDays: 12,
            tasks: [0, 9],
            team: [SAM],
            health: CloseHealth.WATCHING,
        },
        {
            name: "Annual 1099 filing",
            entityIndex: 2,
            stageIndex: 1,
            daysInStage: 9,
            deliveryInDays: 2,
            tasks: [3, 7],
            team: [MORGAN, AVERY],
            unreadTaskCount: 1,
        },
        {
            name: "August close",
            entityIndex: 2,
            stageIndex: null,
            tasks: [2, 10],
            team: [RILEY],
            isCanceled: true,
        },
    ],
];

/**
 * The Demo Firm's clients with their applications, dated from `today`. Ids
 * end in the application's number, which picks its demo viewers; `copy`
 * keeps them unique when stress mode repeats the set.
 */
const createDemoWorkspaceRows = (today: Date, copy: number) =>
    DEMO_CLIENTS.map((client, clientIndex) =>
        createWorkspaceRow({
            name: client.name,
            children: CLIENT_SPECS[clientIndex].map((spec, index) => ({
                ...toWorkRow(spec, today),
                id: `work-${copy}-${clientIndex * 3 + index + 1}`,
            })),
        }),
    );

const STRESS_COPY_COUNT = 170;

/** The demo clients once, or repeated to about 1,000 applications in stress mode. */
export const loadDemoWorkspaceRows = ({
    today,
    isStressMode,
}: {
    today: Date;
    isStressMode: boolean;
}) =>
    Array.from({ length: isStressMode ? STRESS_COPY_COUNT : 1 }, (_, copy) =>
        createDemoWorkspaceRows(today, copy),
    ).flat();
